import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./AdjustDurationDialog.css";

// Presentation only — EventModal.js owns what actually happens with the
// chosen duration/mode/scope on confirm (updating the event, the place's
// Duration, and shifting or recalculating other events if asked). Mirrors
// RecalculateDialog.js's onConfirm-callback shape.
const AdjustDurationDialog = ({ show, onHide, onConfirm, initialMinutes }) => {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  // "off"        — just save this event's new duration.
  // "growShrink" — default: shift every later event by the same delta,
  //                preserving everyone else's own duration exactly.
  // "recalculate"— full rebuild from place data (can overwrite other
  //                events' already-correct durations with generic ones).
  const [mode, setMode] = useState("growShrink");
  const [scope, setScope] = useState("day"); // "day" | "trip"

  useEffect(() => {
    if (show) {
      const total = Math.max(0, initialMinutes ?? 0);
      setHours(Math.floor(total / 60));
      setMinutes(total % 60);
      setMode("growShrink");
      setScope("day");
    }
  }, [show, initialMinutes]);

  const allHours = Array.from({ length: 13 }, (_, i) => i); // 0-12h
  const allMinutes = [0, 5, 10, 15, 20, 30, 40, 45, 50];

  const handleConfirm = () => {
    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes <= 0) return;
    onConfirm({ minutes: totalMinutes, mode, scope });
    onHide();
  };

  return (
    <div className="modal">
      <Modal
        show={show}
        onHide={onHide}
        animation={false}
        dialogClassName="add-dialog"
        aria-labelledby="add-title"
        centered
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="add-shell">

            <div className="add-header">
              <div className="add-header-icon">
                <FontAwesomeIcon icon={solid("hourglass-half")} />
              </div>
              <h2 className="add-title" id="add-title">Adjust Duration</h2>
              <button className="add-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="add-body">

              <div className="add-row">
                <span className="add-label">Duration</span>
                <select
                  className="add-num-select"
                  value={hours}
                  onChange={(e) => setHours(parseInt(e.target.value))}
                >
                  {allHours.map((h) => <option key={h} value={h}>{h} h</option>)}
                </select>
                <select
                  className="add-num-select"
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value))}
                >
                  {allMinutes.map((m) => <option key={m} value={m}>{m} min</option>)}
                </select>
              </div>

              <div className="add-row add-row-wrap">
                <span className="add-label">Other events</span>
                <div className="add-seg add-seg-3">
                  <button
                    className={`add-seg-btn${mode === "off" ? " active" : ""}`}
                    onClick={() => setMode("off")}
                  >
                    Leave As-Is
                  </button>
                  <button
                    className={`add-seg-btn${mode === "growShrink" ? " active" : ""}`}
                    onClick={() => setMode("growShrink")}
                  >
                    Grow/Shrink
                  </button>
                  <button
                    className={`add-seg-btn${mode === "recalculate" ? " active" : ""}`}
                    onClick={() => setMode("recalculate")}
                  >
                    Recalculate
                  </button>
                </div>
              </div>

              {mode !== "off" && (
                <div className="add-row">
                  <span className="add-label">Scope</span>
                  <div className="add-seg">
                    <button
                      className={`add-seg-btn${scope === "day" ? " active" : ""}`}
                      onClick={() => setScope("day")}
                    >
                      This Day
                    </button>
                    <button
                      className={`add-seg-btn${scope === "trip" ? " active" : ""}`}
                      onClick={() => setScope("trip")}
                    >
                      Whole Trip
                    </button>
                  </div>
                </div>
              )}

              {mode === "off" && (
                <p className="add-hint">
                  Saves the new duration without adjusting other events — if it
                  now overlaps the next event, you'll get a reminder to
                  recalculate this day.
                </p>
              )}
              {mode === "growShrink" && (
                <p className="add-hint">
                  Shifts every later event by the same amount you changed this
                  one by — their own durations aren't touched.
                </p>
              )}
              {mode === "recalculate" && (
                <p className="add-hint">
                  Rebuilds times from each place's typical duration — may
                  overwrite other events' durations you've already fine-tuned.
                </p>
              )}

            </div>

            <div className="add-footer">
              <button className="add-btn add-btn-cancel" onClick={onHide}>Cancel</button>
              <button
                className="add-btn add-btn-action"
                onClick={handleConfirm}
                disabled={hours === 0 && minutes === 0}
              >
                Save
              </button>
            </div>

          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdjustDurationDialog;
